import { useEffect } from 'react';
import { MapRef } from 'react-map-gl';
import { debounce } from 'lodash-es';
import { computeDesiredOrder, isAlreadyOrdered } from './layer-order';

// Enforces the centralized map layer z-order (INS-9623).
//
// MapLibre appends runtime-added layers to the top of the stack, so toggling a
// layer remounts its `<Layer>` and breaks the intended hierarchy. This hook,
// mounted once at the map level, re-derives the canonical order from whatever
// layers currently exist and snaps them back into place on every style change —
// self-healing and independent of mount/toggle timing.
export const useEnforceLayerOrder = (mapRef: MapRef | null) => {
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    const enforce = () => {
      const layers = map.getStyle()?.layers ?? [];
      const desired = computeDesiredOrder(layers);
      if (isAlreadyOrdered(layers, desired)) return;

      // Walk top->bottom, placing each layer just below the one above it.
      // moveLayer(id, beforeId) inserts `id` beneath `beforeId`.
      for (let i = desired.length - 2; i >= 0; i--) {
        const current = desired[i];
        const above = desired[i + 1];
        if (map.getLayer(current) && map.getLayer(above)) {
          try {
            map.moveLayer(current, above);
          } catch (e) {
            console.warn(`Could not move layer ${current} below ${above}`, e);
          }
        }
      }
    };

    // A single toggle emits a burst of style/source events; coalesce them.
    const debouncedEnforce = debounce(enforce, 50);

    const onStyleData = () => debouncedEnforce();
    const onSourceData = (e: { isSourceLoaded: boolean }) => {
      if (e.isSourceLoaded) debouncedEnforce();
    };

    if (map.isStyleLoaded()) enforce();
    map.on('styledata', onStyleData);
    map.on('sourcedata', onSourceData);

    return () => {
      map.off('styledata', onStyleData);
      map.off('sourcedata', onSourceData);
      debouncedEnforce.cancel();
    };
  }, [mapRef]);
};
